import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { AddressModel } from '../../../../../../core/models/address.model';
import { AddressService } from '../../../../../../core/services/address.service';
import { EstablishmentModel } from '../../../../../../core/models/establishment.model';
import { EstablishmentService } from '../../establishment.service';
import { FeiColumn } from '../../../../../../core/base/interfaces/fei-column.interface';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { AuthService } from '../../../../../../core/services/auth.service';
import { Sort } from '@angular/material/sort';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { SnackbarService } from '../../../../../../core/services/snackbar.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { BuildingModel } from '../../../../../../core/models/building.model';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionMessageService } from '../../../../../../core/services/permission-message.service';

@Component({
  selector: 'vex-establishment-details-addresses',
  templateUrl: './establishment-details-addresses.component.html',
  styleUrls: ['./establishment-details-addresses.component.scss'],
})
export class EstablishmentDetailsAddressesComponent implements OnInit, OnDestroy {
  establishment: EstablishmentModel;

  displayedColumnsAddress: string[] = [];
  dataSourceAddresses = new MatTableDataSource<AddressModel>();
  sortFieldAddress: string;
  pageIndexAddress = 1;
  sortOrderAddress: string;
  search: string;
  pageSizeAddress = 10;
  addressFilters: any = {};
  countAddresses = 0;
  loadingData = false;

  feisColumnsAddress: Array<FeiColumn> = [
    {
      label: 'Admin.',
      column: 'favorite',
      canFavorite: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_ESTABLISHMENT),
    },
    { label: 'Adresse', column: 'street', width: '25%' },
    { label: 'Complément', column: 'additionalAddress', width: '20%' },
    { label: 'Code postal', column: 'postalCode', width: '15%' },
    { label: 'Ville', column: 'city', width: '20%' },
    { label: 'Pays', column: 'country', width: '15%' },
  ];

  ENUM_PERMISSIONS = ENUM_PERMISSIONS;
  private destroy$ = new Subject<void>();

  // Contexte de navigation de retour
  returnContext: {
    show: boolean;
    buildingId: string;
    buildingLabel: string;
  } = { show: false, buildingId: '', buildingLabel: '' };

  // Flag pour savoir si une modification a été effectuée
  addressModified = false;

  constructor(
    private establishmentService: EstablishmentService,
    private addressService: AddressService,
    public authService: AuthService,
    private snackbarService: SnackbarService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private permissionMessageService: PermissionMessageService,
  ) {
    this.displayedColumnsAddress = this.feisColumnsAddress.map(x => x.column);
  }

  ngOnInit(): void {
    // Détecter le contexte de retour depuis le Router state
    const navigationState = window.history.state;

    if (
      navigationState &&
      navigationState.returnTo === 'building' &&
      navigationState.buildingId &&
      navigationState.buildingLabel
    ) {
      this.returnContext.show = false; // Ne pas montrer tout de suite
      this.returnContext.buildingId = navigationState.buildingId;
      this.returnContext.buildingLabel = navigationState.buildingLabel;
    }

    this.establishmentService.entity$
      .pipe(
        takeUntil(this.destroy$),
        filter(entity => !!entity && !!entity._id),
      )
      .subscribe(entity => {
        this.establishment = entity;
        this.addressFilters.establishmentId = entity._id;
        this.loadAddresses();
      });
  }

  loadAddresses(): void {
    this.loadingData = true;
    this.addressService
      .findAll(this.pageIndexAddress, this.pageSizeAddress, this.sortFieldAddress, this.sortOrderAddress, this.search, {
        ...this.addressFilters,
        requiredFields: ['buildingsInfo', 'favorite'],
      })
      .subscribe({
        next: response => {
          const enriched = (response.data || []).map(address => {
            const building = address.buildingsInfo.find(b =>
              b.establishments.some(b => b._id === this.establishment._id),
            );

            return {
              ...address,
              building,
            } as AddressModel & { favorite: boolean; building: BuildingModel };
          });

          this.dataSourceAddresses = new MatTableDataSource<AddressModel>(enriched);
          this.countAddresses = response.totalData || enriched.length;
          this.loadingData = false;
        },
        error: () => {
          this.loadingData = false;
        },
      });
  }

  pageEvent(event: PageEvent): void {
    this.pageIndexAddress = event.pageIndex + 1;
    this.pageSizeAddress = event.pageSize;
    this.loadAddresses();
  }

  sortEvent(event: Sort): void {
    this.sortFieldAddress = event.active;
    this.sortOrderAddress = event.direction;
    this.pageIndexAddress = 1;
    this.loadAddresses();
  }

  onSearchChange(event: string): void {
    this.search = event;
    this.pageIndexAddress = 1;
    this.delayLoadAddresses(400);
  }

  clearTextFilter(): void {
    this.search = '';
    this.pageIndexAddress = 1;
    this.loadAddresses();
  }

  delayLoadAddresses(delay: number): void {
    setTimeout(() => this.loadAddresses(), delay);
  }

  async favoriteAddressEstablishment(element: any) {
    if (this.permissionMessageService.blockIfMissingPermission(ENUM_PERMISSIONS.UPDATE_ESTABLISHMENT)) {
      return;
    }
    this.establishmentService.updateMainAddress(this.establishment._id, element._id, element.favorite).subscribe(() => {
      this.snackbarService.success('Adresse principale mise à jour avec succès');
      this.loadAddresses();

      // Marquer la modification comme effectuée et afficher la bannière si contexte présent
      this.addressModified = true;
      if (this.returnContext.buildingId && this.returnContext.buildingLabel) {
        this.returnContext.show = true;
      }
    });
  }

  /**
   * Navigue vers le bâtiment d'origine
   */
  navigateToBuilding() {
    this.router.navigateByUrl(`/management/building/${this.returnContext.buildingId}`).then();
  }

  /**
   * Ferme la bannière manuellement
   */
  dismissBanner(): void {
    this.returnContext.show = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
